import Button from "@/components/Button";
import { ControlledTextField as TextField } from "@/components/TextField";
import useModal from "@/hooks/useModal";
import {
  FieldPathByValue,
  FormProvider,
  useController,
  UseControllerProps,
  useForm,
  UseFormReturn
} from "react-hook-form";
import { CREATE_PERSON_MUTATION, CreatePersonInput, PHONE_EXISTS_QUERY } from "../queries";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { useNavigate } from "@/router";
import E164PhoneNumberInput from "@/components/E164PhoneNumberInput";
import CountryDropdown from "@/components/AddressAutoComplete/CountryDropdown";
import AddressAutocompleteInput from "@/components/AddressAutoComplete";
import { AddressFields } from "@/components/AddressAutoComplete/utils";
import { useEffect, useState } from "react";
import Banner from "@/components/Banner";
import TipCard from "@/components/TipCard";
import { emailPattern } from "@/lib/emailHelpers";
import StateInput from "@/components/AddressAutoComplete/StateInput";
import classNames from "classnames";
import useAddressAutocomplete from "@/hooks/useAddressAutocompelte";
import PostalCodeInput from "@/components/AddressAutoComplete/PostalCodeInput";
import { onBlurDialogKeyboard, onFocusDialogKeyboard } from "@/lib/mobileHelpers";
import { showSnackbar } from "@/lib/snackbarUtils";

const defaultValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  countryCode: "USA",
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  isVerified: false
};

type FieldValues = typeof defaultValues;

function PhoneField<Name extends FieldPathByValue<FieldValues, string>>({
  control,
  name,
  label,
  setError,
  trigger,
  clearErrors
}: UseControllerProps<FieldValues, Name> & { label: string } & Pick<
    UseFormReturn<FieldValues>,
    "setError" | "trigger" | "clearErrors"
  >) {
  const {
    field: { onChange, value },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ name, control });
  return (
    <E164PhoneNumberInput
      label={label}
      value={value}
      onChange={onChange}
      error={!!error}
      errorText={error?.message}
      disabled={isSubmitting}
      onError={(error) => {
        if (error) {
          setError(name, { message: error });
        } else {
          clearErrors(name);
          trigger(name);
        }
      }}
    />
  );
}

const requiredAddressKeys: (keyof AddressFields)[] = ["address1", "city", "state", "postalCode"];
const allAddressKeys = requiredAddressKeys.concat("address2");

function toInput(fieldValues: FieldValues, ignoreDuplicatePhone?: boolean): CreatePersonInput {
  const { phone, address1, address2, city, state, postalCode, countryCode, isVerified, ...fields } = fieldValues;
  let input: CreatePersonInput = fields;

  if (phone) input.phone = { phoneNumber: phone, phoneType: "mobile", ignoreDuplicate: ignoreDuplicatePhone };

  if (address1 && city && state && postalCode && countryCode) {
    input.address = {
      address1,
      city,
      state,
      postalCode,
      isVerified,
      countryCode,
      addressType: "home"
    };
    if (address2) input.address.address2 = address2;
  }

  return input;
}

function anyAddressFields(values: FieldValues) {
  let key: keyof FieldValues;
  for (key of allAddressKeys) {
    if (values[key]) return true;
  }
  return false;
}

function allRequiredAddressFields(values: FieldValues) {
  let key: keyof FieldValues;
  for (key of requiredAddressKeys) {
    if (!values[key]) return false;
  }
  return true;
}

const requiredIfAnyAddressFields = {
  validate: { required: (value: string, fields: FieldValues) => !(!value && anyAddressFields(fields)) }
};

const onlyAsPartOfAddress = {
  validate: (value: string, values: FieldValues) => !(value && !allRequiredAddressFields(values))
};

function AddressForm({ control }: { control: UseFormReturn<FieldValues>["control"] }) {
  const { isUSA, ...autocompleteProps } = useAddressAutocomplete<FieldValues>({ mapName: (name) => name });
  return (
    <>
      <div className="my-6">
        <CountryDropdown control={control} name="countryCode" />
      </div>

      <AddressAutocompleteInput
        label="Street Address"
        name="address1"
        {...autocompleteProps}
        control={control}
        rules={requiredIfAnyAddressFields}
      />

      <TextField
        className="mt-6 w-full"
        control={control}
        name="address2"
        label="Street Address Line 2"
        rules={onlyAsPartOfAddress}
      />

      <div className="mt-6 flex flex-col sm:flex-row gap-6 sm:gap-2">
        <TextField
          onBlur={() => onBlurDialogKeyboard("people")}
          onFocus={(event) => onFocusDialogKeyboard("people", event)}
          className={classNames("flex-col", { "flex-[5]": isUSA })}
          control={control}
          name="city"
          label="City"
          rules={requiredIfAnyAddressFields}
        />

        <div className={classNames({ "flex-[2]": isUSA })}>
          <StateInput control={control} name="state" isUSA={isUSA} rules={requiredIfAnyAddressFields} />
        </div>

        <PostalCodeInput
          onBlur={() => onBlurDialogKeyboard("people")}
          onFocus={(event) => onFocusDialogKeyboard("people", event)}
          className={classNames({ "flex-[2]": isUSA })}
          isUSA={isUSA}
          control={control}
          name="postalCode"
          rules={requiredIfAnyAddressFields}
        />
      </div>
    </>
  );
}

export default function CreateModal({ onClosed }: { onClosed(): void }) {
  const { Modal, close } = useModal();
  const [mutate] = useMutation(CREATE_PERSON_MUTATION);
  const [checkPhoneExists] = useLazyQuery(PHONE_EXISTS_QUERY);
  const form = useForm({ defaultValues, mode: "onBlur" });
  const {
    handleSubmit,
    control,
    formState: { isValid, errors, isSubmitting },
    setError,
    getValues,
    watch
  } = form;
  const navigate = useNavigate();
  const [bannerError, setBannerError] = useState("");
  const [duplicatePhone, setDuplicatePhone] = useState(false);

  const phone = watch("phone");

  useEffect(() => {
    if (phone) {
      checkPhoneExists({ variables: { input: phone } }).then(({ data }) => {
        setDuplicatePhone(Number(data?.people.totalCount) > 0);
      });
    }
  }, [phone, checkPhoneExists]);

  const submit = handleSubmit(async (fields: FieldValues) => {
    try {
      const response = await mutate({ variables: { input: toInput(fields, duplicatePhone) } });

      const { person, message = "", code } = response.data?.createPerson || {};
      if (person?.pgaId) {
        showSnackbar(message || "Person created");
        close();
        navigate("/people/:pgaId/person/contact", { params: { pgaId: person.pgaId } });
      } else if (code === 409 && message.toLowerCase().includes("email")) {
        setError("email", { type: code.toString(), message: "Person already exists with this email" });
      } else if (code === 409 && message.toLowerCase().includes("phone")) {
        setDuplicatePhone(true);
      } else {
        throw new Error(message || "Something went wrong");
      }
    } catch (error) {
      setBannerError(`Error: ${(error as ApolloError).message}`);
    }
  });

  const emailIsTaken = errors.email?.type === "409";

  return (
    <Modal
      headline="Create Person"
      data-testid="create-person-modal"
      onClosed={onClosed}
      className="h-fit max-h-[90vh]"
      action={
        <Button
          disabled={!isValid || isSubmitting}
          onClick={submit}
          iconClassName={classNames({ "[&>md-icon]:animate-spin": isSubmitting })}
          icon={isSubmitting ? "progress_activity" : "check"}
        >
          {isSubmitting ? "Saving" : "Save"}
        </Button>
      }
    >
      {!!bannerError && (
        <div className="mb-6">
          <Banner inline variant="error" description="The person could not be created." subText={bannerError} />
        </div>
      )}

      <FormProvider {...form}>
        <div className="flex gap-2">
          <TextField className="flex-grow" control={control} name="firstName" label="First Name" required />
          <TextField className="flex-grow" control={control} name="lastName" label="Last Name" required />
        </div>

        <TextField
          className="w-full mt-6"
          control={control}
          name="email"
          label="Email"
          type="email"
          required
          rules={{ pattern: emailPattern }}
        />

        {emailIsTaken && (
          <div className="mt-6">
            <TipCard
              onClick={() => navigate({ pathname: "/search", search: `q=${encodeURIComponent(getValues("email"))}` })}
            />
          </div>
        )}

        <div className="my-6">
          <PhoneField name="phone" label="Mobile" {...form} />
        </div>

        {duplicatePhone && (
          <TipCard
            header="Mobile Number Match Found"
            text="This mobile number is already associated with a person in the system."
            onClick={() => navigate({ pathname: "/search", search: `q=${encodeURIComponent(getValues("phone"))}` })}
          />
        )}

        <AddressForm control={control} />
      </FormProvider>
    </Modal>
  );
}
